import { useCallback, useMemo } from 'react';
import { Scene } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { isLayoutDefault } from '@goldcast/player-config/src/utils/layout';
import { ORIGINAL_VIDEO_ID } from '@goldcast/player-config/src/constants/screenshare';
import {
  selectSpeakerDetails,
  selectVideoFeeds
} from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { updateClipData } from '../../../ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../../../ClipNewTimeline/clipUpdateDataUtils/constants';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { buildSceneTracks, type SpeakerTimelineTrack } from '@/stores/contentMetaDataStore/utils/sceneTracks';

export type { SpeakerTimelineTrack };

export function useGlobalVideoToggle({
  broadcastId,
  clipId,
  scenes
}: {
  broadcastId: string;
  clipId: string;
  scenes: Scene[];
}) {
  const speakerDetails = useContentMetadataStore(selectSpeakerDetails(broadcastId));
  const videoFeeds = useContentMetadataStore(selectVideoFeeds(broadcastId));

  const tracksToRender = useMemo(() => {
    // The original-layout scene only carries the single stitched recording, so prefer a scene that
    // actually splits the participants out. Fall back to the first scene when none does.
    const sceneToRender = scenes.find(scene => !isLayoutDefault(scene.details.layout)) ?? scenes[0];
    const tracks = buildSceneTracks({ scene: sceneToRender, videoFeeds, speakerDetails });

    if (tracks.length <= 1) {
      return tracks;
    }
    const isSceneWithOriginalLayout = isLayoutDefault(sceneToRender.details.layout);
    return tracks.filter(track =>
      isSceneWithOriginalLayout ? track.id === ORIGINAL_VIDEO_ID : track.id !== ORIGINAL_VIDEO_ID
    );
  }, [scenes, videoFeeds, speakerDetails]);

  const handleTrackToggle = useCallback(
    (toggledTrackKey: string) => {
      // Force the new value (rather than letting each scene invert its own current state) so a
      // single click in the global layouts popup adds/removes the track consistently across every
      // scene, matching the toggle icon's displayed value.
      const toggledTrack = tracksToRender.find(track => track.toggleKey === toggledTrackKey);
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY,
        trackName: toggledTrackKey,
        forceSelected: toggledTrack ? !toggledTrack.selected : undefined
      });
    },
    [clipId, tracksToRender]
  );

  return {
    tracksToRender,
    handleTrackToggle
  };
}
